import React from 'react';

import RecordBadge from '../common/RecordBadge';
import { formatCount, joinMeta } from '../../utils/counts';
import { formatBytes } from '../../utils/formatters';
import { formatDisplayDate } from '../../utils/dateDisplay';

/**
 * What a transfer offer is (KPR-743): the collection's badge — not a link,
 * because the person it is offered to cannot open it until they own it — and
 * what it holds, counted when it was offered. Shared by the offer page and
 * Home's "Offered to you" rows.
 */

/** "Olive Owner (olive@example.com)" — who made the offer; an administrator for a handover. */
export function offeredBy(offer) {
	if (offer?.kind === 'handover') return 'An administrator';
	const name = String(offer?.from?.name || '').trim();
	const email = String(offer?.from?.email || '').trim();
	if (name && email) return `${name} (${email})`;
	return name || email || 'Someone';
}

/** "1 sub-collection · 3 items · 4.0 MB of files" — never a zero (an empty one says so). */
export function holdsText(offer) {
	const parts = joinMeta(
		formatCount(offer?.subCollectionCount, 'sub-collection'),
		formatCount(offer?.items, 'item'),
		Number(offer?.storageBytes) > 0 ? `${formatBytes(offer.storageBytes)} of files` : null,
	);
	return parts || 'Nothing in it yet';
}

export default function TransferOfferSummary({ offer, size = 'md', showSubCollections = false }) {
	const subs = Array.isArray(offer?.subCollections) ? offer.subCollections : [];
	const more = Math.max(0, (Number(offer?.subCollectionCount) || 0) - subs.length);
	return (
		<div className="transfer-offer-summary">
			<RecordBadge kind="collection" record={offer?.collection || {}} title={offer?.collection?.name || 'A collection'} size={size} />
			<p className="transfer-offer-summary__meta">{holdsText(offer)}</p>
			{showSubCollections && subs.length ? (
				<p className="transfer-offer-summary__subs">
					{`With ${subs.map(sub => sub.name).join(', ')}${more ? ` and ${more} more` : ''}.`}
				</p>
			) : null}
			{offer?.expiresAt && offer?.status === 'pending' ? (
				<p className="transfer-offer-summary__meta">{`The offer ends on ${formatDisplayDate(offer.expiresAt)}.`}</p>
			) : null}
		</div>
	);
}
